import { computed, h } from "vue";
import CountryFlag from "@/components/CountryFlag.vue";
import { TournamentIcon } from "@xs2event/js-lib/icons";
import { isoCountry2, SportType } from "@xs2event/js-lib";
import { useI18n } from "vue-i18n";
import { useEvents, useTeams } from "@xs2event/js-lib/api";
import { slugify } from "@/helpers/format";
import { CUSTOM_ISO_COUNTRIES } from "@/constants/menuItems";
import { eventsWithTeam, eventsWithSportType, eventsWithCountry, eventsWithTournament } from "@/constants/routes";

// Builds the entries of one main menu item's submenu (football countries with their teams and leagues, other
// events per sport, tennis tournaments in grand slam order, ...). Shared by the desktop mega menu and the mobile menu.
export function useMainMenuEntries(item) {
  const { locale, t } = useI18n();

  // FIXME: Add Enums
  const countriesCustomOrder = ["ESP", "GBR", "ITA", "DEU", "FRA", "NLD", "PRT", "USA", "AUT"]; // ISO 3-letter codes

  const uniqueCountries = new Set<string>(
    item?.items?.[0].items
      ?.map((x) => x.region)
      .sort((a, b) => {
        if (a === "WORLD") return 1;
        if (b === "WORLD") return -1;

        const indexA = countriesCustomOrder.indexOf(a);
        const indexB = countriesCustomOrder.indexOf(b);

        if (indexA === -1 && indexB === -1) return a.localeCompare(b);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;

        return indexA - indexB;
      })
  );

  const today = new Date().toISOString().split("T")[0];

  // All football teams of the menu's countries, not only the popular ones.
  const teamsQuery = useTeams(
    computed(() => ({
      isoCountry: `in:[${Array.from(uniqueCountries).join(",")}]`,
      sportType: "soccer",
      pageSize: 1000,
      eventStartdate: `ge:${today}`,
    })),
    {
      enabled: item.label == "Football",
      staleTime: 1000 * 60 * 60 * 3,
    }
  );

  // Upcoming events with tickets, to keep only the teams that play a home match
  const eventsQuery = useEvents(
    computed(() => ({
      sportType: SportType.FOOTBALL,
      country: `in:[${Array.from(uniqueCountries).join(",")}]`,
      dateStart: `ge:${today}`,
      pageSize: 9999,
      ticketsAvailable: "ge:1",
    })),
    {
      enabled: item.label == "Football",
      staleTime: 1000 * 60 * 60 * 3,
    }
  );

  const teamsWithHomeEvents = computed(() => {
    if (!eventsQuery.data.value?.events || !teamsQuery.data.value?.teams) return [];

    const homeTeamIds = new Set(eventsQuery.data.value.events.map((event) => event.hometeamId).filter(Boolean));
    return teamsQuery.data.value.teams
      .filter((team) => homeTeamIds.has(team.teamId) && !team.officialName.includes("test"))
      .sort((a, b) => a.officialName.localeCompare(b.officialName));
  });

  const countries = computed(() => {
    return Object.fromEntries(
      Array.from(uniqueCountries).map((key) => {
        return [
          key,
          {
            teams: teamsWithHomeEvents.value.filter((x) => x.isoCountry == key),
            popularTournaments: item.items[0].items.filter((x) => x.region == key),
          },
        ];
      })
    );
  });

  // Other tournaments and leagues link based on the other tournaments
  const buildWorldTournamentLink = (tournaments) => {
    const tournamentIds = [
      tournaments
        .map((tournament) => {
          return tournament?.to?.query?.tournament;
        })
        .filter(Boolean),
    ];
    return tournamentIds.length
      ? eventsWithTournament(tournamentIds.join(","), { sport_type: "football" })
      : eventsWithSportType(SportType.FOOTBALL);
  };

  const items = computed(() => {
    let _items;

    if (item.label === "Other events") {
      // Map each sport: tournaments first, then buttons at the end
      const mapped = item.items.map((item) => {
        const sportType = item.params?.sport_type || item.label?.toLowerCase().replace(/\s/g, "");

        // Separate buttons from regular items
        const regularItems = (item.items || []).filter((i) => !i.button);
        const existingButtons = (item.items || []).filter((i) => i.button);

        // For "Other events", always link to events page (not tournaments page)
        // Only tennis and football have tournaments pages
        const eventsLink = `/events?sport_type=${sportType}`;

        return {
          ...item,
          to: eventsLink, // Always link to events page for "Other events" sports
          items: [
            // First: all tournaments/events items
            ...regularItems,
            // Then: "View all events" button
            {
              label: t("View all events"),
              to: `/events?sport_type=${sportType}`,
              button: true,
            },
            // Keep any existing buttons at the very end
            ...existingButtons,
          ],
        };
      });
      _items = mapped.sort((a, b) => a.label.localeCompare(b.label));
    } else if (item.label == "Football") {
      const mappedCountries = Object.entries(countries.value!).map(([key, value]) => {
        const entry = {
          to:
            key == "WORLD"
              ? buildWorldTournamentLink(value.popularTournaments)
              : eventsWithCountry(key.toLowerCase(), { sport_type: "football" }),
          icon:
            key == "WORLD"
              ? h(TournamentIcon, { class: "size-7 p-1.5 shadow-md text-black rounded-full" })
              : h(CountryFlag, { code: key, class: "shadow-md" }),
          // ISO 3 code for the desktop mega menu, which renders its own (smaller) flag instead of `icon`.
          flag: key == "WORLD" ? undefined : key,
          label:
            key == "WORLD"
              ? "Other leagues"
              : (() => {
                  const country = isoCountry2.find((x) => x.code3 == key);
                  const regionCode = country?.code || key;
                  let displayName;
                  try {
                    displayName = new Intl.DisplayNames([locale.value.replace("_", "-")], { type: "region" }).of(
                      regionCode
                    );
                  } catch {
                    displayName = new Intl.DisplayNames(["en"], { type: "region" }).of(regionCode);
                  }
                  return displayName || key;
                })(),
          items: [
            value.teams.length && {
              label: "Teams",
              items: value.teams.map((team) => ({
                label: team.officialName,
                to: eventsWithTeam(slugify(team.slug), { sport_type: "football" }),
              })),
            },
            value.popularTournaments.length && {
              label: "Leagues",
              items: value.popularTournaments
                .map(
                  (tournament) =>
                    tournament.number_events && {
                      label: tournament.label,
                      to: { ...tournament.to, query: { ...tournament.to.query, sport_type: "football" } },
                      // Use icon from tournament if available, otherwise check label
                      icon:
                        tournament.icon ||
                        (tournament.label?.toLowerCase().includes("champions league") ||
                        tournament.label?.toLowerCase().includes("europa league") ||
                        tournament.label?.toLowerCase().includes("conference league") ||
                        tournament.label?.toLowerCase().includes("uefa champions") ||
                        tournament.label?.toLowerCase().includes("uefa europa") ||
                        tournament.label?.toLowerCase().includes("uefa conference")
                          ? TournamentIcon
                          : undefined),
                      // Preserve ranking for sorting
                      ranking: tournament.ranking || tournament.ranking === 0 ? tournament.ranking : 0,
                    }
                )
                .filter(Boolean)
                .sort((a, b) => {
                  // First: sort by ranking (higher ranking first), if ranking exists
                  const aRanking = a.ranking !== undefined && a.ranking !== null ? a.ranking : 0;
                  const bRanking = b.ranking !== undefined && b.ranking !== null ? b.ranking : 0;
                  if (aRanking !== bRanking) {
                    return bRanking - aRanking; // Higher ranking first
                  }

                  // Second: Special handling: swap Conference League and Europa League
                  const aIsConference = a.label?.toLowerCase().includes("conference league");
                  const bIsConference = b.label?.toLowerCase().includes("conference league");
                  const aIsEuropa = a.label?.toLowerCase().includes("europa league");
                  const bIsEuropa = b.label?.toLowerCase().includes("europa league");

                  // If one is Conference League and the other is Europa League, swap them
                  if (aIsConference && bIsEuropa) return -1; // Conference League comes before Europa League
                  if (aIsEuropa && bIsConference) return 1; // Europa League comes after Conference League

                  // Third: Sort tournaments: non-numbered first (e.g., "Bundesliga"), then numbered (e.g., "2. Bundesliga")
                  const aStartsWithNumber = /^\d/.test(a.label);
                  const bStartsWithNumber = /^\d/.test(b.label);

                  if (aStartsWithNumber && !bStartsWithNumber) return 1;
                  if (!aStartsWithNumber && bStartsWithNumber) return -1;

                  // Finally: If both start with numbers or both don't, sort alphabetically
                  return a.label.localeCompare(b.label);
                }),
            },
          ].filter(Boolean),
        };

        // The desktop mega menu shows the international leagues as a featured column instead of an "Other leagues"
        // country; the mobile menu keeps the country.
        if (key != "WORLD") return entry;
        const leagues = entry.items.find((group) => group.label === "Leagues");
        return { ...entry, featured: { label: "Featured tournaments", links: leagues?.items ?? [] } };
      });

      // "Other leagues" (WORLD) is sorted last within mappedCountries; insert the
      // hardcoded custom countries (e.g. Scotland) directly before it so they
      // appear above "Other leagues" instead of after it.
      const otherLeaguesIndex = mappedCountries.findIndex((c) => c.label === "Other leagues");
      const countriesWithCustom =
        otherLeaguesIndex === -1
          ? [...mappedCountries, ...CUSTOM_ISO_COUNTRIES]
          : [
              ...mappedCountries.slice(0, otherLeaguesIndex),
              ...CUSTOM_ISO_COUNTRIES,
              ...mappedCountries.slice(otherLeaguesIndex),
            ];

      _items = [
        ...countriesWithCustom,
        ...(item || {}).items.filter((x) => x.label != "Competitions"),
        { label: t("View all football events"), to: eventsWithSportType("football"), button: true },
      ];
    } else if (item.label == "Tennis") {
      // Sort the Grand Slams first, then the other tournaments in alphabetical order
      const sr = (arr) => arr.sort((a, b) => a.label.localeCompare(b.label));
      const tournaments = item.items[0].items;
      const grandSlamsNames = ["Roland Garros", "Wimbledon", "Australian Open", "US Open"];
      const grandSlams = sr(tournaments.filter((t) => grandSlamsNames.includes(t.label)));
      const others = sr(tournaments.filter((t) => !grandSlams.includes(t)));
      _items = [...grandSlams, ...others];
    } else {
      _items = item.items;
    }

    _items = _items?.filter((item) =>
      item.items ? item.items.some((x) => ("number_events" in x ? x.number_events : true)) : true
    );

    if (_items?.length == 1) {
      _items = _items[0].items || _items;
    }

    return _items;
  });

  const isLoading = computed(() => teamsQuery.isFetching.value);

  return { entries: items, isLoading };
}
